import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import { IEmotionBubble, emotionBubbleStylesMapped } from "../../../models/interactions/EmotionBubble";
import Interaction from "../../../models/interactions/Interaction";
import NodeActorPropDropdown from "../components/NodeActorPropDropdown";
import NodeStringDropdown from "../components/NodeStringDropdown";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import MessageBubbleAnimationDropdown from "../../previews/message_bubble/MessageBubbleAnimationDropdown";
import LookatTargetDropdown from "./LookatTargetDropdown";
import { useTranslation } from "react-i18next";
import usePropHelper from "../../../hooks/PropHelper";
import { getActorPositionIdInTake } from "../EnvironmentPositionContext";
import { hasLookatTarget } from "../../../models/NoLookat";

export default function EmotionBubbleNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const { t } = useTranslation();
  const emotionBubble = props.interaction as IEmotionBubble;
  const { updateInteraction, sequence } = useUpdateSequenceInteraction();
  const take = useSelector(selectSelectedTake3D);
  const { getBubbleTargetProps } = usePropHelper();
  const propsInTake = useMemo(
    () =>
      take
        ? getBubbleTargetProps(take, {
            kind: "sequence",
            nodeId: props.nodeId,
            sequence,
          })
        : [],
    [getBubbleTargetProps, props.nodeId, sequence, take]
  );
  const isPropIdInTake = (objectId: string) =>
    propsInTake.some((prop) => prop.id === objectId);
  const isPropAnchor = isPropIdInTake(emotionBubble.npcId);
  // In sequences lookAt is a position id; the anchor's own position is never a valid target.
  const isSelfLookAt =
    !!emotionBubble.lookAt &&
    emotionBubble.lookAt === getActorPositionIdInTake(take, emotionBubble.npcId);

  useEffect(() => {
    if (!emotionBubble.lookAt) return;
    if (!isPropAnchor && !isSelfLookAt) return;
    updateInteraction(props.nodeId, {
      ...emotionBubble,
      lookAt: "",
    } as IEmotionBubble);
  }, [emotionBubble, isPropAnchor, isSelfLookAt, props.nodeId, updateInteraction]);

  const actorDropdown = useMemo(
    () => (
      <NodeActorPropDropdown
        label="Actor"
        selectedObjectId={emotionBubble.npcId}
        availableProps={propsInTake}
        onClick={(objectId: string) => {
          const mustClearLookAt =
            isPropIdInTake(objectId) ||
            (!!emotionBubble.lookAt &&
              emotionBubble.lookAt === getActorPositionIdInTake(take, objectId));
          updateInteraction(props.nodeId, {
            ...emotionBubble,
            npcId: objectId,
            lookAt: mustClearLookAt ? "" : emotionBubble.lookAt,
          } as IEmotionBubble);
        }}
      />
    ),
    [emotionBubble, propsInTake, take, updateInteraction, props.nodeId]
  );

  const lookAtDropdown = useMemo(
    () => (
      <LookatTargetDropdown
        label="Look at"
        nodeId={props.nodeId}
        selectedTargetId={emotionBubble.lookAt}
        allowKeepCurrent
        allowNoActor
        disabled={isPropAnchor}
        excludeActorId={emotionBubble.npcId}
        onClick={(targetId: string) =>
          updateInteraction(props.nodeId, {
            ...emotionBubble,
            lookAt: targetId,
          } as IEmotionBubble)
        }
      />
    ),
    [isPropAnchor, emotionBubble, updateInteraction, props.nodeId]
  );

  // How the speaker looks at the listener: whole body (finalIK false, default) or head only.
  // Only shown when there is a listener and the speaker is an actor.
  const wholeBodyLabel = String(t("look_mode.whole_body"));
  const headOnlyLabel = String(t("look_mode.head_only"));
  const lookModeDropdown = useMemo(
    () =>
      !isPropAnchor && hasLookatTarget(emotionBubble.lookAt) ? (
        <NodeStringDropdown
          label={String(t("look_mode.label"))}
          items={[wholeBodyLabel, headOnlyLabel]}
          selectedItem={emotionBubble.finalIK ? headOnlyLabel : wholeBodyLabel}
          onClick={(item: string) =>
            updateInteraction(props.nodeId, {
              ...emotionBubble,
              finalIK: item === headOnlyLabel,
            } as IEmotionBubble)
          }
        />
      ) : null,
    [isPropAnchor, emotionBubble, updateInteraction, props.nodeId, wholeBodyLabel, headOnlyLabel, t]
  );

  const animationDropdown = useMemo(
    () => (
      <MessageBubbleAnimationDropdown
        selectedAnimation={emotionBubble.animation}
        variant="node"
        onClick={(animation) =>
          updateInteraction(props.nodeId, {
            ...emotionBubble,
            animation: animation,
          } as IEmotionBubble)
        }
      />
    ),
    [emotionBubble.animation, updateInteraction, props.nodeId]
  );

  const emotionStyleDropdown = useMemo(
    () => (
      <NodeStringDropdown
        label="Emotion Style"
        items={emotionBubbleStylesMapped.map(style => style.name)}
        selectedItem={emotionBubble.messageBubbleStyle}
        onClick={(style: string) =>
          updateInteraction(props.nodeId, {
            ...emotionBubble,
            messageBubbleStyle: style,
          } as IEmotionBubble)
        }
      />
    ),
    [emotionBubble.messageBubbleStyle, updateInteraction, props.nodeId]
  );

  return (
    <div className="w-full h-full flex flex-col gap-[4px]">
      {actorDropdown}
      {lookAtDropdown}
      {lookModeDropdown}
      {animationDropdown}
      {emotionStyleDropdown}
    </div>
  );
}
